/**
 * The single seam between the SPA and fpa-backend. Components never call fetch() directly.
 *
 * - Prepends `env.apiUrl`, sends/receives JSON.
 * - Attaches `Authorization: Bearer <token>` from the registered token provider.
 * - Throws a typed `ApiError` on any non-2xx. 401 also notifies the auth-error handler (silent
 *   refresh, then re-login). 403 is NOT a session problem — callers branch on `isForbidden`.
 * - In mock mode the transport is swapped for the in-browser mock API (see src/api/mock).
 */
import { env } from '@/config/env'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API request failed with status ${status}`)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  /** The server's `message` (string, or the first of a list), when it sent one. */
  get serverMessage(): string | null {
    const message = (this.body as { message?: unknown } | null)?.message
    if (typeof message === 'string' && message.trim()) return message
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0]
    return null
  }
}

/** A user-facing message for any error: the server's message when there is one, else `fallback`. */
export function errorMessage(error: unknown, fallback: string): string {
  return (isApiError(error) && error.serverMessage) || fallback
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

type TokenProvider = () => string | null | Promise<string | null>
type AuthErrorHandler = (error: ApiError) => void
export type Transport = (input: string, init: RequestInit) => Promise<Response>

let tokenProvider: TokenProvider | null = null
let authErrorHandler: AuthErrorHandler | null = null
let transport: Transport = (input, init) => fetch(input, init)

export function setAuthTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider
}

export function setAuthErrorHandler(handler: AuthErrorHandler | null): void {
  authErrorHandler = handler
}

/** Swap the HTTP transport (mock API, tests). Pass null to restore `fetch`. */
export function setTransport(next: Transport | null): void {
  transport = next ?? ((input, init) => fetch(input, init))
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export function buildUrl(path: string, params?: QueryParams): string {
  const url = `${env.apiUrl}${path.startsWith('/') ? path : `/${path}`}`
  if (!params) return url
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.append(key, String(value))
  }
  const query = qs.toString()
  return query ? `${url}?${query}` : url
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  params?: QueryParams
  body?: unknown
  signal?: AbortSignal
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  const token = tokenProvider ? await tokenProvider() : null
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await transport(buildUrl(path, options.params), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  const body = await parseBody(response)
  if (!response.ok) {
    const error = new ApiError(response.status, body)
    if (error.isUnauthorized) authErrorHandler?.(error)
    throw error
  }
  return body as T
}
